import { useCentralizedMe } from '@crehana/auth';
import type { TimeAndAttendanceUserOvertimeRequestDetailQuery } from '../use-request-detail/get-overtime-request-detail.v5.generated';
import { TimeAndAttendanceUserOvertimeRequestDetailDocument } from '../use-request-detail/get-overtime-request-detail.v5.generated';
import { useTimeAndAttendanceUserOvertimeRequestCommentAddMutation } from './add-overtime-request-comment.v5.generated';

export type TAddCommentResult =
  | 'success'
  | 'required'
  | 'tooLong'
  | 'forbidden'
  | 'error';

const COMMENT_REQUIRED_CODE = 'OVERTIME_REQUEST_COMMENT_REQUIRED';
const COMMENT_TOO_LONG_CODE = 'OVERTIME_REQUEST_COMMENT_TOO_LONG';

// The response comment is appended to the cached detail as the server stored it
// (`created_at` included). No local copy: the backend says not to build one.
export const useAddRequestComment = (requestId: string | null) => {
  const { currentPageOrganization } = useCentralizedMe();
  const orgId = currentPageOrganization?.id ?? 0;

  const [addMutation, { loading: submitting }] =
    useTimeAndAttendanceUserOvertimeRequestCommentAddMutation();

  const addComment = async (body: string): Promise<TAddCommentResult> => {
    if (!requestId) {
      return 'error';
    }
    const input = {
      centralized_organization_id: orgId,
      overtime_request_id: requestId,
    };
    try {
      const { data } = await addMutation({
        variables: { input: { ...input, comment: body } },
        context: { clientName: 'v5' },
        update: (cache, { data: mutationData }) => {
          const added =
            mutationData?.time.overtime_requests.add_overtime_request_comment;
          if (added?.__typename !== 'TimeAddOvertimeRequestCommentPayload') {
            return;
          }
          cache.updateQuery<TimeAndAttendanceUserOvertimeRequestDetailQuery>(
            {
              query: TimeAndAttendanceUserOvertimeRequestDetailDocument,
              variables: { input },
            },
            previous => {
              const detail =
                previous?.time.overtime_requests.get_overtime_request_detail;
              if (
                !previous ||
                detail?.__typename !== 'TimeGetOvertimeRequestDetailPayload'
              ) {
                return undefined;
              }
              return {
                ...previous,
                time: {
                  ...previous.time,
                  overtime_requests: {
                    ...previous.time.overtime_requests,
                    get_overtime_request_detail: {
                      ...detail,
                      overtime_request: {
                        ...detail.overtime_request,
                        comments: [
                          ...detail.overtime_request.comments,
                          added.comment,
                        ],
                      },
                    },
                  },
                },
              };
            },
          );
        },
      });
      const result = data?.time.overtime_requests.add_overtime_request_comment;

      if (result?.__typename === 'TimeAddOvertimeRequestCommentPayload') {
        return 'success';
      }
      if (result?.__typename === 'ForbiddenErrorResponse') {
        return 'forbidden';
      }
      if (result?.__typename === 'ValidationErrorResponse') {
        if (result.code === COMMENT_REQUIRED_CODE) {
          return 'required';
        }
        if (result.code === COMMENT_TOO_LONG_CODE) {
          return 'tooLong';
        }
      }
      return 'error';
    } catch {
      return 'error';
    }
  };

  return { addComment, submitting };
};
